/**
 * `rating` — `RatingValue` (`specs/M2-tier2-column-types.md` §5.10, `[09§A.7 rating]`).
 *
 * `value` is `{"rating":5,"changed_at":…}` and `text` is the bare number (`"5"`). The scale is
 * `settings.limit` — *"between 3 and 10. Defaults to 5"* — and the glyph is
 * `settings.symbol` ∈ `stars` | `hearts`; this account has a `hearts` column at `#579bfc`
 * `[cvs§2 rating]`.
 *
 * A rating **out of range** is refused rather than clamped: monday's own editor cannot produce
 * one, and silently changing a number the caller sent would lose data.
 */
import { z } from 'zod';
import { defaultFilterSql, defaultSummarySql } from '../sql.js';
import { textShadow, toFragmentTimestamp } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
export const RATING_MIN_LIMIT = 3;
export const RATING_MAX_LIMIT = 10;
export const RATING_DEFAULT_LIMIT = 5;
export const ratingValueSchema = z.looseObject({
    rating: z.number().int().min(1),
    changed_at: z.string().nullable().optional(),
});
import { ratingFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
export const ratingSettingsSchema = z.looseObject({
    limit: z.number().int().min(RATING_MIN_LIMIT).max(RATING_MAX_LIMIT).optional(),
    symbol: z.string().optional(),
    color: z.string().optional(),
    hide_footer: z.boolean().optional(),
});
export function ratingLimit(settings) {
    return settings.limit ?? RATING_DEFAULT_LIMIT;
}
function normalize(raw, settings, keepVolatile) {
    if (raw === null || raw === undefined)
        return null;
    const source = typeof raw === 'object' ? raw.rating : raw;
    if (source === null || source === undefined || source === '')
        return null;
    const rating = typeof source === 'number' ? source : Number(String(source).trim());
    if (!Number.isFinite(rating) || !Number.isInteger(rating)) {
        throw new ColumnValueError('rating', `"${String(source)}" is not a whole number`);
    }
    // Filtering uses `0` for blank `[09§A.7 rating]`, and so does a cleared cell.
    if (rating === 0)
        return null;
    const limit = ratingLimit(settings);
    if (rating < 1 || rating > limit) {
        throw new ColumnValueError('rating', `rating ${rating} is outside 1..${limit}`);
    }
    const value = { rating };
    if (keepVolatile && typeof raw === 'object') {
        const source = raw;
        if ('changed_at' in source)
            value.changed_at = source.changed_at;
    }
    return value;
}
export const ratingColumn = defineColumnType({
    type: 'rating',
    title: 'Rating',
    description: 'A score out of a fixed scale, drawn as stars or hearts.',
    apiFragmentName: 'RatingValue',
    legacyTypeName: 'rating',
    idPrefix: 'rating',
    valueSchema: ratingValueSchema,
    settingsSchema: ratingSettingsSchema,
    defaultSettings: { limit: RATING_DEFAULT_LIMIT, symbol: 'stars' },
    settingsStrSchema: ratingSettingsSchema,
    writable: true,
    supportsSimpleValue: true,
    clearForms: ['null', 'empty-string'],
    filterConditions: ratingFilterConditions,
    filterOperators: operatorsOf(ratingFilterConditions),
    aggregations: ['avg', 'sum', 'min', 'max', 'median', 'count', 'count_empty', 'none'],
    emptyText: '',
    parseValue(raw, settings) {
        return normalize(raw, settings, false);
    },
    parseSimpleValue(raw, settings) {
        return normalize(raw, settings, false);
    },
    toText(value) {
        return value === null ? '' : String(value.rating);
    },
    toSortKeys(value) {
        if (value === null)
            return { text: null, number: null, date: null };
        return { text: textShadow(String(value.rating)), number: value.rating, date: null };
    },
    isEqual(a, b) {
        if (a === null || a === undefined)
            return b === null || b === undefined;
        if (b === null || b === undefined)
            return false;
        return a.rating === b.rating;
    },
    toActivityValue(value) {
        return value === null ? null : { rating: value.rating };
    },
    fromMondayValue(raw, settings) {
        if (raw === null || raw === undefined)
            return null;
        // Import must never lose a cell: a rating above the current `limit` is kept as-is (the
        // column's scale can be lowered after the fact), unlike `parseValue`.
        const source = typeof raw === 'object' ? raw.rating : raw;
        if (source === null || source === undefined || source === '')
            return null;
        const rating = typeof source === 'number' ? source : Number(String(source).trim());
        if (!Number.isFinite(rating) || rating === 0)
            return null;
        void settings;
        const value = { rating: Math.trunc(rating) };
        if (typeof raw === 'object') {
            const source = raw;
            if ('changed_at' in source)
                value.changed_at = source.changed_at;
        }
        return value;
    },
    toMondayValue(value) {
        if (value === null)
            return null;
        const out = { rating: value.rating };
        if (value.changed_at !== undefined)
            out.changed_at = value.changed_at;
        return out;
    },
    toSimpleValue(value) {
        return value === null ? '' : String(value.rating);
    },
    fromMondaySettings(raw) {
        return ratingSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    toApiFragment(value) {
        if (value === null)
            return {};
        return { rating: value.rating, updated_at: toFragmentTimestamp(value.changed_at) };
    },
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    bucketsOf(value) {
        return value === null ? [] : [{ key: value.rating, label: String(value.rating) }];
    },
    filterSql(ctx) {
        return defaultFilterSql('number', ctx);
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map